import { graphql } from "@/config";
import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import Cookies from "universal-cookie";
import type { AuthClient } from "@/lib/auth";

// HTTP Link
const httpLink = createHttpLink({
  uri: graphql.url,
});

// Auth Link Factory
const createAuthLink = (authClient: AuthClient) => {
  return setContext(async (_, { headers }) => {
    const cookies = new Cookies();
    let token = cookies.get("auth-token");

    // Try to get a fresh token from the auth client if we have a stored token
    if (token && typeof window !== "undefined") {
      try {
        const freshToken = await authClient.getCurrentToken();
        if (freshToken) {
          token = freshToken;
          // Update the cookie with the fresh token
          cookies.set("auth-token", freshToken, { path: "/" });
        }
      } catch (error) {
        // If getting fresh token fails, continue with the stored token
        console.warn("Failed to get fresh token, using stored token:", error);
      }
    }

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });
};

// Error Link Factory
const createErrorLink = (authClient: AuthClient) => {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}, Extensions: ${JSON.stringify(extensions)}`,
        );

        // Handle specific GraphQL errors
        if (extensions?.code === "UNAUTHENTICATED") {
          // Redirect to auth page for authentication errors
          if (typeof window !== "undefined") {
            window.location.href = "/auth";
          }
        }
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);

      // Handle authentication errors
      if ("statusCode" in networkError && networkError.statusCode === 401) {
        // Check if this is a token expiration error
        if (
          networkError.message?.includes("Token has expired") ||
          networkError.message?.includes("token expired")
        ) {
          // Try to refresh the token using the auth client
          const refreshToken = async () => {
            try {
              // Try to get a fresh token
              const freshToken = await authClient.getCurrentToken();

              if (freshToken) {
                // Update the cookie with the fresh token
                const cookies = new Cookies();
                cookies.set("auth-token", freshToken, { path: "/" });

                // Retry the failed operation with the new token
                return forward(operation);
              }
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
            }

            // If refresh failed, redirect to auth page
            if (typeof window !== "undefined") {
              window.location.href = "/auth";
            }
          };

          // Execute the refresh
          void refreshToken();
        } else {
          // For other 401 errors, redirect to auth page
          if (typeof window !== "undefined") {
            window.location.href = "/auth";
          }
        }
      }
    }
  });
};

// Apollo Client Factory
export const createApolloClient = (authClient: AuthClient) => {
  const authLink = createAuthLink(authClient);
  const errorLink = createErrorLink(authClient);

  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            returns: {
              keyArgs: ["merchantId"],
              merge(existing, incoming) {
                const merged = existing ? { ...existing } : {};
                const results = [
                  ...(existing?.results ?? []),
                  ...(incoming.results ?? []),
                ];

                return {
                  ...merged,
                  ...incoming,
                  results,
                };
              },
            },
            shipments: {
              keyArgs: false,
              merge(existing = [], incoming) {
                return [...existing, ...incoming];
              },
            },
          },
        },
        Shipment: {
          fields: {
            id: {
              read(id) {
                return id;
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });
};
